'use client';

/**
 * The appointment full view — a real page for a single appointment.
 *
 * The calendar keeps its side panel: skimming and editing a booking without
 * losing your place in the day is what that panel is for, and a page cannot do
 * it. This is the other half — somewhere to *read* an appointment, with room
 * for the clinical note, the payment history and the consent trail that the
 * panel has no space for.
 *
 * Renders through `components/app/entity-view` (A17), the same shell as the
 * client record, so the two do not drift into different headers.
 *
 * The title is `Service (Client Name)`. The entity is the appointment; the
 * patient is who it is for. Titled with her name alone it reads as a second
 * client record.
 */

import { AppointmentFormsPanel } from '@/features/consent-forms/components/appointment-forms-panel';
import { useNavigate } from '@tanstack/react-router';
import { differenceInMinutes, format } from 'date-fns';
import {
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  MoreHorizontalIcon,
  PencilIcon,
  UserIcon,
  VideoIcon,
  XCircleIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { AppointmentClientLink } from './appointment-client-link';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { EntityView, EntityViewAside } from '@/components/app/entity-view';
import { StateError } from '@/components/app/state-error';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useActiveOrganization } from '@/features/organization';
import { useGetPractitioner } from '@/features/practitioners/api/get-practitioner';
import { isProductionRuntime } from '@/lib/app-env';
import { zonedEvent } from '@/lib/timezone';
import { useResolvedRoutes } from '@/lib/use-routes';

import { useGetAppointment } from '../api/get-appointment/get-appointment.hook';
import { appointmentStatusLabels } from '../api/types';
import { useUpdateAppointment } from '../api/update-appointment';
import { AppointmentRescheduleDialog } from './appointment-reschedule-dialog';
import { ClinicalPanel } from './appointment-view-clinical';
import { ActivityPanel, PaymentsPanel } from './appointment-view-panels';
import { ForfeitConfirmDialog } from './forfeit-confirm-dialog';
import { wouldForfeitDeposit } from './forfeit-decision';

/**
 * Overview and Forms & consent read real data. Notes, Payments and
 * Activity render the FIXTURE panels (`appointment-view-panels.tsx`,
 * `appointment-view-clinical.tsx`) drawn for design review — fake deposits and activity. They must not show in production, so
 * the fixture tabs are dropped there and kept everywhere else.
 */
const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'clinical', label: 'Notes' },
  { id: 'payments', label: 'Payments' },
  { id: 'consent', label: 'Forms & consent' },
  { id: 'activity', label: 'Activity' },
];

const visibleTabs = () =>
  isProductionRuntime()
    ? TABS.filter((t) => t.id === 'overview' || t.id === 'consent')
    : TABS;

export function AppointmentView({ appointmentId }: { appointmentId: string }) {
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const { data: organization } = useActiveOrganization();
  const { appointment, isLoading, isError, refetch } =
    useGetAppointment(appointmentId);
  /**
   * The treating practitioner is `practitionerId`. `assignedTo` is the user
   * who owns the record, which is often the clinic owner, not who sees the
   * client.
   */
  const { practitioner } = useGetPractitioner({
    id: appointment?.practitionerId ?? '',
  });
  const [tab, setTab] = useState('overview');
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  /**
   * Cancelling moves the booking to `cancelled`; it does not delete it.
   * The status change releases the slot and any room hold, applies the
   * deposit policy and keeps the visit in the client's history. The page
   * stays open so the Cancelled badge confirms what happened.
   */
  const { updateAppointment, isUpdating } = useUpdateAppointment({
    onSuccess: () => setCancelling(false),
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !appointment) {
    return (
      <StateError
        message="Failed to load this appointment. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  const timeZone = organization?.timezone ?? 'UTC';
  const isCancelled = appointment.status === 'cancelled';
  const practitionerName = appointment.practitionerId
    ? (practitioner?.name ?? null)
    : null;
  const practitionerRow = appointment.practitionerId
    ? (practitionerName ?? '—')
    : 'Unassigned';
  const cancelForfeitsDeposit = wouldForfeitDeposit({
    action: 'cancelled',
    deposit: appointment.deposit,
    startDate: appointment.startDate,
    policy: organization,
  });
  const cancel = () =>
    updateAppointment({ id: appointmentId, status: 'cancelled' });
  const start = zonedEvent(appointment.startDate, timeZone);
  const end = zonedEvent(appointment.endDate, timeZone);
  const minutes = differenceInMinutes(end, start);

  const patient = appointment.lead
    ? `${appointment.lead.firstName} ${appointment.lead.lastName ?? ''}`.trim()
    : 'Walk-in';
  /**
   * `Service (Client)` only when we HAVE a service relation. Falling back to
   * `appointment.title` and appending the patient produced "Skin Consultation —
   * Niamh Murphy (Niamh Murphy)": a free-text title already names the client,
   * so it is used verbatim instead.
   */
  const serviceName = appointment.service?.name ?? null;
  const serviceLabel = serviceName ?? appointment.title ?? 'Appointment';
  const heading = serviceName ? `${serviceName} (${patient})` : serviceLabel;

  return (
    <EntityView
      config={{
        identity: {
          title: heading,
          initials: initialsFor(patient),
          subtitle: `${format(start, 'EEE d MMM')} · ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}${
            practitionerName ? ` · ${practitionerName}` : ''
          }`,
          status: appointment.status ? (
            <Badge variant="secondary">
              {
                appointmentStatusLabels[
                  appointment.status as keyof typeof appointmentStatusLabels
                ]
              }
            </Badge>
          ) : undefined,
        },
        back: {
          label: 'Back to calendar',
          onClick: () => navigate({ to: routes.calendar }),
        },
        actions: (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={isCancelled}
              onClick={() => setRescheduling(true)}
            >
              <PencilIcon className="size-3.5" />
              Reschedule
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" aria-label="More actions">
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  disabled={isCancelled}
                  onSelect={() => setCancelling(true)}
                >
                  <XCircleIcon className="size-4" />
                  Cancel booking
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ),
        tabs: visibleTabs(),
        activeTab: tab,
        onTabChange: setTab,
        aside: (
          <EntityViewAside>
            <div className="space-y-3">
              <Fact
                icon={ClockIcon}
                label="Duration"
                value={`${minutes} min`}
              />
              {practitionerName ? (
                <Fact
                  icon={UserIcon}
                  label="Practitioner"
                  value={practitionerName}
                />
              ) : null}
              {appointment.deposit ? (
                <Fact
                  icon={CheckIcon}
                  label="Deposit"
                  value={
                    appointment.deposit.amountCents === null ||
                    appointment.deposit.reconciliationRequired
                      ? 'Amount unknown. Reconcile payment.'
                      : `${formatMoney(appointment.deposit.amountCents as number, appointment.deposit.currency)} · ${appointment.deposit.status}`
                  }
                />
              ) : null}
              {appointment.virtualMeetingUrl ? (
                <Fact
                  icon={VideoIcon}
                  label="Location"
                  value={
                    <>
                      Online ·{' '}
                      <a
                        href={appointment.virtualMeetingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary underline underline-offset-2"
                      >
                        Join Google Meet
                      </a>
                    </>
                  }
                />
              ) : null}
              <Fact
                icon={CalendarIcon}
                label="Booked"
                value={format(
                  zonedEvent(appointment.createdAt, timeZone),
                  'd MMM yyyy'
                )}
              />
            </div>
          </EntityViewAside>
        ),
      }}
    >
      {tab === 'overview' ? (
        <Overview
          rows={[
            {
              label: 'Service',
              value: `${serviceLabel} · ${minutes} min`,
            },
            {
              label: 'Practitioner',
              value: practitionerRow,
            },
            {
              label: 'When',
              value: `${format(start, 'EEEE d MMMM')}, ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`,
            },
            {
              label: 'Client',
              value: appointment.lead ? (
                <>
                  <AppointmentClientLink
                    leadId={appointment.lead.id}
                    name={patient}
                  />
                  {appointment.lead.phone ? ` · ${appointment.lead.phone}` : ''}
                </>
              ) : (
                'Walk-in'
              ),
            },
          ]}
        />
      ) : null}
      {tab === 'clinical' && !isProductionRuntime() ? (
        <ClinicalPanel patient={patient} start={start} />
      ) : null}
      {tab === 'payments' && !isProductionRuntime() ? <PaymentsPanel /> : null}
      {tab === 'consent' ? (
        <AppointmentFormsPanel appointmentId={appointmentId} />
      ) : null}
      {tab === 'activity' && !isProductionRuntime() ? (
        <ActivityPanel start={start} />
      ) : null}

      <AppointmentRescheduleDialog
        appointmentId={appointment.id}
        startDate={appointment.startDate}
        endDate={appointment.endDate}
        open={rescheduling}
        onOpenChange={setRescheduling}
      />

      {cancelForfeitsDeposit && appointment.deposit ? (
        <ForfeitConfirmDialog
          open={cancelling}
          onOpenChange={setCancelling}
          action="cancelled"
          deposit={appointment.deposit}
          busy={isUpdating}
          onConfirm={cancel}
        />
      ) : (
        <ConfirmDeleteDialog
          open={cancelling}
          onOpenChange={setCancelling}
          icon={XCircleIcon}
          title="Cancel this booking?"
          description={
            <>
              The booking is marked cancelled and stays in {patient}'s history.
              The slot is released immediately and can be booked by someone
              else.
            </>
          }
          confirmLabel="Cancel booking"
          cancelLabel="Keep booking"
          isPending={isUpdating}
          onConfirm={cancel}
        />
      )}
    </EntityView>
  );
}

function initialsFor(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof ClockIcon;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <div className="break-words text-sm">{value}</div>
      </div>
    </div>
  );
}

function Overview({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <section className="overflow-hidden rounded-xl border bg-background">
      <div className="divide-y">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex flex-wrap items-baseline gap-x-6 gap-y-1 px-5 py-4"
          >
            <span className="w-32 shrink-0 text-muted-foreground text-sm">
              {row.label}
            </span>
            <span className="min-w-0 flex-1 text-sm">{row.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
